import { usePrettyLogEventPage } from "@components/v2/analytics/hooks";
import {
  GRANT_PARTNER_CONSENT,
  REVOKE_PARTNER_CONSENT,
} from "@gql/partner-consent";
import { useNorthbeamMutation } from "@hooks/http";
import {
  type Partner,
  type GrantPartnerConsent,
  type GrantPartnerConsentVariables,
  type RevokePartnerConsent,
  type RevokePartnerConsentVariables,
  type PartnerPermissionEnum,
} from "@north-beam/nb-client-side";

export const useUpdatePartnerConsent = ({
  permission,
}: {
  permission: PartnerPermissionEnum;
}) => {
  const permissions = [permission];
  const prettyLogEventPage = usePrettyLogEventPage();

  const [grantPartnerConsentMutation, { loading: isGrantingConsent }] =
    useNorthbeamMutation<GrantPartnerConsent, GrantPartnerConsentVariables>(
      GRANT_PARTNER_CONSENT,
    );

  const [revokePartnerConsentMutation, { loading: isRevokingConsent }] =
    useNorthbeamMutation<RevokePartnerConsent, RevokePartnerConsentVariables>(
      REVOKE_PARTNER_CONSENT,
    );

  const dispatchEvent = (partner: Partner, action: "granted" | "revoked") => {
    prettyLogEventPage({
      domain: "PartnerConsent",
      action: "onClick",
      additionalActionInfo: action,
      data: {
        partner,
        action,
        permissions,
      },
    });
  };

  const grantPartnerConsent = async (partner: Partner) => {
    dispatchEvent(partner, "granted");
    return grantPartnerConsentMutation({
      variables: {
        partner: partner,
        permissions,
      },
    });
  };

  const revokePartnerConsent = async (partner: Partner) => {
    dispatchEvent(partner, "revoked");
    return revokePartnerConsentMutation({
      variables: {
        partner: partner,
        permissions,
      },
    });
  };

  const isUpdatingConsent = isGrantingConsent || isRevokingConsent;
  return {
    grantPartnerConsent,
    revokePartnerConsent,
    isUpdatingConsent,
  };
};
